"use client";

import { useEffect } from "react";

/**
 * Lightweight scroll-triggered reveal system.
 * Replaces Framer Motion whileInView with a single IntersectionObserver.
 * Elements with .reveal / .reveal-left / .reveal-right / .reveal-scale
 * get .revealed added when they enter the viewport.
 * Zero React re-renders — pure DOM class toggling.
 */
export function ScrollRevealInit() {
  useEffect(() => {
    // Respect reduced motion preference
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const selector = ".reveal, .reveal-left, .reveal-right, .reveal-scale";

    if (prefersReduced) {
      document.querySelectorAll(selector).forEach((el) => {
        el.classList.add("revealed");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.1, rootMargin: "-40px" }
    );

    // Observe existing elements
    document.querySelectorAll(selector).forEach((el) => observer.observe(el));

    // Also observe dynamically added elements via MutationObserver
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement) {
            if (node.matches(selector)) observer.observe(node);
            node.querySelectorAll(selector).forEach((el) => observer.observe(el));
          }
        }
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return null;
}
